/* Finish-guided teaching model. Progression, spectral bands, and angle curves are
 * illustrative choices based on the owner's observations, not measurements.
 * The balanced two-field construction preserves E = E1 + E2 and power = |E|².
 */
((root,factory)=>{
  if(typeof module==='object'&&module.exports)module.exports=factory(require('./zmw-thin-film.js'));
  else root.ZMWFinishModel=factory(root.ZMWThinFilm);
})(typeof window==='undefined'?globalThis:window,optics=>{
  'use strict';if(!optics)return null;
  // Legacy thickness identifiers denote unitless progression coordinates here.
  const MAX_THICKNESS=350,REFERENCE_ANGLE=25;
  const FINISHES=Object.freeze([
    {name:'Silver',hex:'#DFDFDF',thickness:25,angleSensitivity:3.3},
    {name:'Gold',hex:'#F7FF00',thickness:90,angleSensitivity:2.4},
    {name:'Purple',hex:'#9900FF',thickness:165,angleSensitivity:1.05},
    {name:'Light blue',hex:'#00A1FF',thickness:245,angleSensitivity:.28},
    {name:'Gray',hex:'#7D7B7B',thickness:325,angleSensitivity:.6}
  ].map(Object.freeze));
  // One progression, with brief transitions rather than repeating color orders.
  const TRANSITIONS=Object.freeze([[45,62],[125,140],[190,203],[282,297]].map(Object.freeze));
  const SHADE=Object.freeze([
    [[0,1.16],[25,1],[45,.96]],
    [[62,1.10],[90,1],[125,.84]],
    [[140,1.12],[165,1],[190,.84]],
    [[203,.60],[245,1],[282,1.35]],
    [[297,.42],[310,.95],[325,1],[350,.9]]
  ]);
  const clamp=(x,a=0,b=1)=>Math.max(a,Math.min(b,x));
  const smooth=t=>t*t*(3-2*t);
  const gaussian=(w,center,width)=>Math.exp(-.5*((w-center)/width)**2);
  const interpolate=(points,x)=>{
    if(x<=points[0][0])return points[0][1];
    for(let i=1;i<points.length;i++)if(x<=points[i][0]){const[a,b]=[points[i-1],points[i]],t=(x-a[0])/(b[0]-a[0]);return a[1]+t*(b[1]-a[1]);}
    return points[points.length-1][1];
  };
  const decode=x=>x<=.04045?x/12.92:((x+.055)/1.055)**2.4;
  const encode=x=>Math.round(255*(clamp(x)<=.0031308?12.92*clamp(x):1.055*clamp(x)**(1/2.4)-.055));
  const css=rgb=>'rgb('+rgb.map(encode).join(', ')+')';
  const hexRGB=hex=>[1,3,5].map(i=>decode(parseInt(hex.slice(i,i+2),16)/255));
  const shadeRGB=(rgb,gain)=>gain<=1?rgb.map(x=>x*gain):rgb.map(x=>x+(1-x)*clamp((gain-1)*.45));
  const angleGain=(index,angle)=>{
    const ratio=Math.cos(clamp(angle,0,65)*Math.PI/180)/Math.cos(REFERENCE_ANGLE*Math.PI/180);
    return ratio**FINISHES[index].angleSensitivity;
  };
  const describe=(thickness,angle=REFERENCE_ANGLE)=>{
    const d=clamp(thickness,0,MAX_THICKNESS);let from=4,to=4,mix=0;
    for(let i=0;i<TRANSITIONS.length;i++){
      const[start,end]=TRANSITIONS[i];
      if(d<start){from=to=i;break;}
      if(d<=end){from=i;to=i+1;mix=smooth((d-start)/(end-start));break;}
    }
    const transitioning=from!==to&&mix>0&&mix<1,dominant=mix>.5?to:from;
    const gain=index=>interpolate(SHADE[index],d)*angleGain(index,angle);
    const a=shadeRGB(hexRGB(FINISHES[from].hex),gain(from)),b=shadeRGB(hexRGB(FINISHES[to].hex),gain(to));
    const color=css(a.map((x,i)=>x*(1-mix)+b[i]*mix));
    return{thickness:d,from,to,mix,dominant,transitioning,gainFrom:gain(from),gainTo:gain(to),color,
      name:transitioning?FINISHES[from].name+' → '+FINISHES[to].name:FINISHES[dominant].name};
  };
  const band=(index,w)=>{
    if(index===0)return .82;
    if(index===1)return .003+.93*gaussian(w,580,31);
    if(index===2)return .003+.94*gaussian(w,425,23)+.32*gaussian(w,650,27);
    if(index===3)return .003+.95*gaussian(w,475,18);
    return .20;
  };
  const strength=(thickness,angle,wavelength)=>{
    const p=describe(thickness,angle);
    return clamp(band(p.from,wavelength)*p.gainFrom*(1-p.mix)+band(p.to,wavelength)*p.gainTo*p.mix,0,.98);
  };
  const sample=(thickness,angle,wavelength)=>{
    const reflectance=strength(thickness,angle,wavelength),phase=2*Math.acos(Math.sqrt(reflectance));
    const surface=[-.5,0],film=[-.5*Math.cos(phase),-.5*Math.sin(phase)];
    const reflected=[surface[0]+film[0],surface[1]+film[1]];
    if(thickness===0){surface[0]=surface[1]=0;film[0]=reflected[0];film[1]=reflected[1];}
    return{wavelength,thickness,angle,surface,film,reflected,reflectance,phase,filmWavelength:wavelength/optics.OXIDE_INDEX};
  };
  const outputColor=(thickness,angle,mode,wavelength)=>{
    if(mode==='white')return describe(thickness,angle).color;
    const rgb=optics.wavelengthColor(wavelength).match(/[\d.]+/g).map(x=>decode(Number(x)/255));
    return css(rgb.map(x=>x*strength(thickness,angle,wavelength)));
  };
  const visibility=power=>.004+.956*clamp(power)**1.65;
  const extremum=(angle,wavelength,target,currentThickness)=>{
    const candidates=Array.from({length:MAX_THICKNESS+1},(_,d)=>({d,r:strength(d,angle,wavelength)}));
    const best=target==='min'?Math.min(...candidates.map(x=>x.r)):Math.max(...candidates.map(x=>x.r));
    return candidates.filter(x=>Math.abs(x.r-best)<.0005).sort((a,b)=>Math.abs(a.d-currentThickness)-Math.abs(b.d-currentThickness))[0].d;
  };
  return Object.freeze({MAX_THICKNESS,REFERENCE_ANGLE,FINISHES,TRANSITIONS,describe,angleGain,strength,sample,outputColor,visibility,extremum});
});
